import React, { useEffect, useState } from 'react'
import { Row, Col, ButtonToolbar } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useParams, useHistory } from 'react-router'
import { useSelector, useDispatch } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faSave,
  faArrowLeft,
  faPlusCircle,
  faSpinner,
  faSortAlt,
  faUndo,
  faEye,
} from '@fortawesome/pro-solid-svg-icons'
import * as fromLessonActions from 'store/actions/lessons-admin.action'
import * as fromCourseActions from 'store/actions/courses.action'
import { IMainState } from 'store/states'
import { ILessonModel, lessonInitialStore } from 'models/lesson.model'
import Loader from 'components/ui/loader/Loader'
import Breadcrumb, { BreadcrumbItem } from 'design-system/breadcrumb'
import Button from 'design-system/button'
import { ButtonVariant, ButtonSizes } from 'design-system/button/src/Button'
import ConfirmationModal from 'components/ui/modals/confirmation/ConfirmationModal'
import { canBeHere } from 'helpers/access.helper'
import { AccessTypesEnum } from 'enum/access.enum'
import { CourseStateType } from 'enum'
import LessonList from './components/lessons-list/LessonList'
import styles from './Index.module.scss'
import PublishButton from './components/PublishButton'
import Wrapper from 'components/reusables/wrapper'

const Index: React.FC = () => {
  const { t } = useTranslation()

  const { courseId } = useParams<Record<'courseId', string>>()
  const dispatch = useDispatch()
  const history = useHistory()

  const { course } = useSelector(({ courses }: IMainState) => courses)
  const { loading, showWarning } = useSelector(
    ({ lessons }: IMainState) => lessons
  )

  const [showLoading, setShowLoading] = useState(loading)
  const [order, setOrder] = useState([lessonInitialStore])
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false)
  const [selected, setSelected] = useState(0)
  const [buttonLoading, setButtonLoading] = useState(false)
  const [draggable, setDraggable] = useState(false)
  const [auxList, setAuxList] = useState([] as any)
  const [lessons, setLessons] = useState([] as any)
  const [contentCount, setContentCount] = useState([])
  const { permission } = useSelector(({ user }: IMainState) => user)
  const [hasResponses, setHasResponses] = useState(false)

  const isImparted = course.state === CourseStateType.Imparted

  useEffect(() => {
    if (permission.moderator)
      canBeHere(courseId, [
        AccessTypesEnum.canEdit,
        AccessTypesEnum.canManageCourses,
      ])
  }, [courseId, permission])

  useEffect(() => {
    setShowLoading(loading)
  }, [loading])

  useEffect(() => {
    return () => {
      setButtonLoading(false)
      dispatch({
        type: fromLessonActions.SHOW_WARNING_LESSON,
        payload: undefined,
      })
    }
  }, [dispatch])

  useEffect(() => {
    dispatch({ type: fromCourseActions.GET_ADMIN_COURSE, payload: courseId })
  }, [courseId, dispatch])

  useEffect(() => {
    if (course.lessons) {
      setAuxList(course.lessons.slice())
      setLessons(course.lessons)
      setContentCount(course.lessonContent)
      setHasResponses(course.hasResponses || false)
    }
  }, [course])

  useEffect(() => {
    if (
      showWarning === undefined &&
      hasResponses &&
      course.id === Number(courseId)
    )
      dispatch({ type: fromLessonActions.SHOW_WARNING_LESSON, payload: true })
  }, [course.id, courseId, dispatch, hasResponses, showWarning])

  const handleSubmit = () => {
    if (order.length > 1)
      dispatch({ type: fromLessonActions.LESSONS_ORDER_EDIT, payload: order })
    setButtonLoading(true)
    history.push('/administration/courses/')
  }

  const handleDelete = (lessonId: number) => {
    setShowDeleteConfirmationModal(false)
    setSelected(0)
    dispatch({ type: fromLessonActions.LESSONS_DESTROY, lessonId, courseId })
  }

  const onDeleteClick = (id: number) => {
    setShowDeleteConfirmationModal(true)
    setSelected(id)
  }

  const handleNewLesson = () => {
    setShowLoading(true)
    // Dispatch con redirect
    dispatch({
      type: fromLessonActions.CREATE_LESSON,
      payload: { courseId, lesson: lessonInitialStore, history },
    })
  }

  const handleCancelDraggable = () => {
    setAuxList(course.lessons.slice())
    setDraggable(false)
  }

  const handleSaveOrder = () => {
    if (order.length > 1)
      dispatch({ type: fromLessonActions.LESSONS_ORDER_EDIT, payload: order })
    setDraggable(false)
  }

  const handlePreview = () => {
    window.open(`/administration/courses/${courseId}/preview`)
  }

  return showLoading ? (
    <Loader overlay color />
  ) : (
    <Wrapper shadow={true}>
      <Row className="pb-4" style={{ margin: '-11px 0 0 0' }}>
        <Breadcrumb separator=">">
          <BreadcrumbItem
            href="/administration/courses"
            text={t('administration.lessons.breadcrum.init')}
          />
          <BreadcrumbItem
            href={`/administration/courses/${courseId}/edit`}
            text={t('administration.lessons.breadcrum.courseData')}
          />
          <BreadcrumbItem
            text={t('administration.lessons.breadcrum.lessons')}
          />
        </Breadcrumb>
      </Row>
      <Row className={'justify-content-center'}>
        <Col sm={12} md={10} lg={8}>
          <h1 className={styles.name}>{course.name}</h1>
          <div className="d-flex justify-content-between align-items-end">
            <div>
              <p className={`m-0 mb-1 mt-2 ${styles.title}`}>
                {t('administration.lessons.curseLessons')}
              </p>
              <p className={'m-0'}>
                {lessons.length > 0
                  ? course.lessons.length > 1
                    ? `${course.lessons.length} ${t(
                        'administration.lessons.lessonsCount.plural'
                      )}`
                    : `${course.lessons.length} ${t(
                        'administration.lessons.lessonsCount.singular'
                      )}`
                  : t('administration.lessons.lessonsCount.noLessons')}
              </p>
            </div>
            <div>
              {!draggable ? (
                <Button
                  className={`m-0 ${styles.orderButton} ${styles.bordeFix} `}
                  variant={ButtonVariant.Secondary}
                  size={ButtonSizes.Small}
                  onClick={() => setDraggable(true)}
                >
                  <FontAwesomeIcon className="mr-2" icon={faSortAlt} />
                  {t('administration.lessons.buttons.moveLessons')}
                </Button>
              ) : (
                <>
                  <Button
                    className={`m-0 mr-1 ${styles.orderButton} ${styles.cancel}`}
                    outline={true}
                    variant={ButtonVariant.Secondary}
                    size={ButtonSizes.Small}
                    onClick={handleCancelDraggable}
                  >
                    <FontAwesomeIcon className="mr-2" icon={faUndo} />
                    {t('administration.lessons.buttons.cancel')}
                  </Button>
                  <Button
                    className={`m-0 ml-1 ${styles.buttonSave} ${styles.orderButton}`}
                    outline={true}
                    variant={ButtonVariant.Success}
                    size={ButtonSizes.Small}
                    onClick={handleSaveOrder}
                  >
                    <FontAwesomeIcon className="mr-2" icon={faSave} />
                    {t('administration.lessons.buttons.saveOrder')}
                  </Button>
                </>
              )}
            </div>
          </div>
        </Col>
      </Row>
      <Row className={'justify-content-center pb-4'}>
        <Col sm={12} md={10} lg={8} className="pb-5">
          {course && course.lessons && course.lessons.length > 0 && (
            <LessonList
              lessons={auxList}
              onDelete={onDeleteClick}
              draggable={draggable}
              onOrderChange={(lessons: ILessonModel[]) => setOrder(lessons)}
              contentCount={contentCount}
              onContentCountChange={(content: any) => setContentCount(content)}
            />
          )}
          <Button
            className={styles.addLessonButton}
            disabled={draggable}
            onClick={() => handleNewLesson()}
          >
            {t('administration.lessons.new')}
            <FontAwesomeIcon icon={faPlusCircle} />
          </Button>
        </Col>
      </Row>
      <Row className={styles.actionButtons}>
        <Col>
          <ButtonToolbar className="float-left">
            <Button
              disabled={draggable}
              size={ButtonSizes.Small}
              variant={ButtonVariant.Secondary}
              onClick={() =>
                !draggable &&
                history.push(`/administration/courses/${courseId}/edit`)
              }
            >
              <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
              {t('administration.lessons.buttons.back')}
            </Button>
          </ButtonToolbar>

          <ButtonToolbar className="float-right ">
            <Button
              disabled={buttonLoading || draggable}
              variant={ButtonVariant.Secondary}
              size={ButtonSizes.Small}
              onClick={() => !buttonLoading && !draggable && handlePreview()}
            >
              <FontAwesomeIcon icon={faEye} className="mr-2" />
              {t('administration.lessons.buttons.preview')}
            </Button>
            <Button
              className="ml-0"
              disabled={buttonLoading || draggable}
              variant={ButtonVariant.Success}
              size={ButtonSizes.Small}
              onClick={() => !buttonLoading && !draggable && handleSubmit()}
            >
              {buttonLoading ? (
                <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
              ) : (
                <FontAwesomeIcon icon={faSave} className="mr-2" />
              )}
              {t('administration.lessons.buttons.save')}
            </Button>
            <PublishButton draggable={draggable} disabled={isImparted} />
          </ButtonToolbar>
        </Col>
      </Row>
      <ConfirmationModal
        show={showDeleteConfirmationModal}
        title=""
        text={t('administration.modal.deleteLectionConfirmation')}
        acceptButtonText={t('components.modal.buttons.delete')}
        onAccept={() => handleDelete(selected)}
        onClose={() => setShowDeleteConfirmationModal(false)}
      />
    </Wrapper>
  )
}

export default Index
